import Logger from "../../shared/logger.js";
import Vue from "vue";
const LOGGER = new Logger(__filename);


/**
 * Validiert eine Benutzereingabe und gibt aufbereitete Informationen über 
 * den Validierungszustand zurück. (u.A. die CSS-Klassen, die in 
 * der UI gesetzt/entfernt werden müssen)
 * 
 * @param {String} rawValue Wert (i.d.R. ein String), der validiert werden soll
 * @param {Object} options Validation-Zustand des Servers, spezielle 
 * 			          Optionen (Wertebereich bei Zahlen, etc). Wird auch die validate-Funktion übergeben.
 * @param {Function} validate Validator-Funktion, die 
 * 				function(rawValue, options){ 
 * 					// let {allowNoValue, from ,to} = options; // Beispiel für Zahlen-Validatoren
 * 					let isValid = ... // TODO: check rawValue is valid according to the given options
 * 					if(isValid){
 * 						return null;
 * 					}else{
 * 						return "Ist nicht gültig, weil ...."; // Fehlermeldung
 * 					}
 *	 			}
 */
function doValidationLogic(rawValue, options, validate) {
	rawValue = valueAsTrimmedString(rawValue);
	let valueChanged = true;
	let invalidForServer = null;
	let invalidMessageServer = "";
	let invalidMessageClient = validate(rawValue, options); // String|null
	let invalidForClient = invalidMessageClient !== null; // true|false  
	if (options.semanticValidation) {
		valueChanged = `${options.semanticValidation.validatedValue}` !== `${rawValue}`;
		invalidMessageServer = options.semanticValidation.message;
		invalidForServer = options.semanticValidation.invalid;
	}
	return _doValidationLogic({
		valueChanged, invalidForServer, invalidMessageServer, invalidMessageClient, invalidForClient
	});
}

/**
 * Hilfsfuntkion! (export nur zum Unit-Testen)
 * @param {*} rawValue 
 * @param {*} options 
 * @param {*} validate 
 */
export function _doValidationLogic({
	valueChanged,
	invalidForServer,
	invalidMessageServer,
	invalidMessageClient,
	invalidForClient
}) {
	let finalMessage = "";
	let finalValid = null; // true|false|null
	if (invalidForClient === false) {
		if (valueChanged) {
			finalValid = null;
		} else {
			if (invalidForServer === true) {
				finalValid = false;
				finalMessage = invalidMessageServer;
			} else if (invalidForServer === false) {
				finalValid = true;
			} else {
				finalValid = null;
			}
		}
	} else {
		finalValid = false;
		finalMessage = invalidMessageClient;
	}

	// build result
	const forbidFormSubmit = invalidForClient === true; // true|false
	const valid = finalValid === true;// true|false
	const invalid = finalValid === false;// true|false
	const neutral = (finalValid !== true) && (finalValid !== false);// true|false
	const invalidMessage = finalMessage || ""; // invalidMessage

	const { classesToSet, classesToUnset } = (function () {
		const classesToSet = [];
		const classesToUnset = [];
		if (forbidFormSubmit) {
			classesToSet.push(classToMarkInvalid);
		} else {
			classesToUnset.push(classToMarkInvalid);
		}
		if (valid) {
			classesToSet.push("is-valid");
			classesToUnset.push("is-invalid");
		}
		if (invalid) {
			classesToSet.push("is-invalid");
			classesToUnset.push("is-valid");
		}
		if (neutral) {
			classesToUnset.push("is-valid");
			classesToUnset.push("is-invalid");
		}
		return { classesToSet, classesToUnset };
	}());

	return {
		forbidFormSubmit,
		valid,
		invalid,
		neutral,
		invalidMessage,
		classesToSet, classesToUnset
	};
}

/**
 * Validiert Eingabefelder und sorgt dafür, dass die notwendigen CSS-Klassen 
 * gesetzt werden. Somit wird der Valdierungszustand in der UI sichtbar und 
 * MC kann erkennen, wann Submit-Buttons deaktiviert werden müssen (wird 
 * ebenfalls automatisch durchgeführt).
 * 
 * ...
 * <input v-mc-validate="computedValidatorArgs">
 * ...
 * {
 * 		computed: {
 *			return {
 *					expect: "double", // Validator-Name, siehe VALIDATORS_BY_TYPE und validatorByExpectedType
 *					
 *					// Optionen für den jeweiligen Validator
 *					from: 1.0,
 *					to: 6.0,
 *					allowNoValue: false, // true|false
 *
 *					onValidationChanged: this.onValidationChanged, // callback
 *					semanticValidation: { // Validierung durch Server (Autorenskript)
 *						invalid: false, // true|false|null
 *						validatedValue: "3.123",
 *						message: "Das ist nicht PI"
 *				}
 *			};
 * 
 * 		},
 * 		methods: {
 * 			onValidationChanged({errorMessage}){ 
 * 				// set data to update the ui
 * 				if(errorMessage){ ... } else { ... }
 * 			}
 * 		}
 * 
 * }
 * 
 */
Vue.directive("mc-validate", {
	bind: function (el, binding) {

		const options = (function () {
			let opt = binding.value;
			return {
				// assign options like opt.from, opt.to, etc
				...opt,
				// override and cleanup options, set defaults
				expect: opt.expect || "",
				allowNoValue: opt.allowNoValue ? true : false,
				semanticValidation: (function () {
					return {
						// defaults
						valid: null,
						validatedValue: null,
						message: null,
						// assign and override defaults
						...(opt.semanticValidation || {})
					};
				}()),
				onValidationChanged: opt.onValidationChanged || function () { }
			};
		}());

		const validate = validatorByExpectedType(options.expect);

		function onChange() {
			let value = el.value;
			let { invalidMessage, classesToSet, classesToUnset } = doValidationLogic(value, options, validate);

			// update elements classes
			el.className = [...el.className.split(" "), ...classesToSet]
				.filter((value, index, self) => self.indexOf(value) === index) // distinct
				.filter(c => !classesToUnset.includes(c))
				.join(" ");

			// revalidate form submit buttons
			revalidateForms();

			// raise event
			options.onValidationChanged({ errorMessage: invalidMessage });
		}
		el.addEventListener("input", onChange);
		Vue.nextTick(onChange);
	}
});


/**
 * CSS class to mark an invalid input field. Will be used
 * by formToJson-Function to decide if form is valid.
 */
export const classToMarkInvalid = "mc-invalid-input";


const formRevalidationBus = new Vue();

/**
 * call this method on a changed input field. used to tell the
 * form submit button to check, if the form can be submtted.
 */
export const revalidateForms = function () {
	formRevalidationBus.$emit("revalidate-forms");
};

/**
 * register callback to check for forms valdiation
 * state (used to enable/disable submit buttons).
 * @param {*} callback
 */
export const onRevalidateForms = function (callback) {
	formRevalidationBus.$on("revalidate-forms", callback);
};




function valueAsTrimmedString(value) {
	if (value === null || value === undefined) {
		return "";
	} else {
		return ("" + value).trim();
	}
}

/**
 * gibt true zurück, wenn eine Ganzzahl ("-12", "0", "13", etc)
 * als String übergeben wurde.
 */
function isInt(value) {
	value = valueAsTrimmedString(value);
	return /^(-|\+)?[0-9]+$/.test(value);
}


/**
 * gibt true zurück, wenn eine Kommazahl ("-5.0", "3.141", "0.0", etc)
 * als String übergeben wurde.
 */
function isFloat(value) {
	value = valueAsTrimmedString(value);
	// let isFloat = "" + parseFloat(value) === "" + value;
	return /^(-|\+)?([0-9]+(\.[0-9]+)?)$/.test(value);
}

/**
 * gibt true zurück, wenn eine Bruch ("-1/2", "1/2", etc)
 * als String übergeben wurde.
 */
function isFrac(value) {
	value = valueAsTrimmedString(value);
	return /^(\+|-)?(\d)+\/(\d)+$/.test(value);
}


export const validateInteger = function (
	value,
	{ from = null, to = null, allowNoValue = false } = {}
) {
	value = valueAsTrimmedString(value);
	LOGGER.log(`validate integer: value=${value} from=${from} to=${to} allowNoValue=${allowNoValue}`);
	if (value === "") {
		if (allowNoValue) {
			return null;
		}
		return "Fehlender Wert";
	}
	if (isInt(value)) {
		value = parseInt(value);
		if ((from !== null) && (value < from)) return "Wert kleiner als erlaubt";
		if ((to !== null) && (value > to)) return "Wert größer als erlaubt";
	} else {
		return "Wert ist keine Ganzzahl";
	}
	return null;
};


export const validateDouble = function (
	value,
	{ from = null, to = null, allowNoValue = false } = {}
) {
	value = valueAsTrimmedString(value);
	value = value.replace(",", ".");  // support "," as separator
	LOGGER.log(`validate double: value=${value} from=${from} to=${to} allowNoValue=${allowNoValue}`);
	if (value === "") {
		if (allowNoValue) {
			return null;
		}
		return "Fehlender Wert";
	}
	if (isFloat(value)) {
		value = parseFloat(value);
		if ((from !== null) && (value < from)) return "Wert kleiner als erlaubt";
		if ((to !== null) && (value > to)) return "Wert größer als erlaubt";
	} else {
		return "Wert ist keine gültige Zahl";
	}
	return null;
};


export const validateFrac = function (
	value,
	{ from = null, to = null, allowNoValue = false } = {}
) {
	value = valueAsTrimmedString(value);
	if (value === "") {
		if (allowNoValue) {
			return null;
		}
		return "Fehlender Wert";
	}
	if (isFrac(value)) {
		let splitted = value.split("/");
		let valueAsDouble = parseInt(splitted[0]) / parseInt(splitted[1]);
		return validateDouble("" + valueAsDouble, { from, to, allowNoValue });
	}
	return "Ist keine gültiger Bruch";
};



export const validateRational = function (
	value,
	{ from = null, to = null, allowNoValue = false } = {}
) {
	value = valueAsTrimmedString(value);
	LOGGER.log(`validate rational: value=${value} from=${from} to=${to} allowNoValue=${allowNoValue}`);
	if (value === "") {
		if (allowNoValue) {
			return null;
		}
		return "Fehlender Wert";
	}
	if (isInt(value)) {
		return validateInteger(value, { from, to, allowNoValue });
	}
	if (isFrac(value)) {
		return validateFrac(value, { from, to, allowNoValue });
	}
	return "Ist keine gültige rationale Zahl";
};



export const validateReal = function (
	value,
	{ from = null, to = null, allowNoValue = false } = {}
) {
	value = valueAsTrimmedString(value);
	value = value.replace(",", ".");  // support "," as separator
	LOGGER.log(`validate real: value=${value} from=${from} to=${to} allowNoValue=${allowNoValue}`);
	if (value === "") {
		if (allowNoValue) {
			return null;
		}
		return "Fehlender Wert";
	}
	if (isInt(value)) {
		return validateInteger(value, { from, to, allowNoValue });
	}
	if (isFloat(value)) {
		return validateDouble(value, { from, to, allowNoValue });
	}
	if (isFrac(value)) {
		return validateFrac(value, { from, to, allowNoValue });
	}
	return "Ist keine gültige reele Zahl";
};


export const validateMcConsole = function ({ input, expectedInput }) {
	let validator = VALIDATORS_BY_TYPE[expectedInput.type] || null;
	if (validator === null) {
		LOGGER.error(`expected input type '${expectedInput.type}' is not supported`);
		return null; // fallback: backend will handle it...
	} else {
		return validator(input);
	}
};

function validateTextInputFromConsole(str) {
	return (str && str.trim().length > 0) ? null : "Die Eingabe ist leer.";
}

function validateTextInput(rawValue, { regex, regexErrorMessage, allowNoValue }) {
	if (rawValue && rawValue.trim().length > 0) {
		// ok, value present
	} else {
		if (allowNoValue) {
			return null; // ok, empty allowed
		} else {
			return "Die Eingabe ist leer.";
		}
	}
	// non empty rawValue is present, check regex
	if (regex) {
		let compiled = new RegExp(regex);
		let matchArray = rawValue.match(compiled);
		if (matchArray === null) {
			if (regexErrorMessage && regexErrorMessage.trim() !== "") {
				return regexErrorMessage;
			}
			return "Die Eingabe entspricht nicht dem geforderten Format";
		}
	}
	return null;
}


/**
 * Validatoren
 */
const VALIDATORS_BY_TYPE = {
	"integer": validateInteger,
	"double": validateDouble,
	"real": validateReal,
	"rational": validateRational,
	"text": validateTextInputFromConsole,
	"text-from-input": validateTextInput
};

/**
 * Gibt den Validator anhand seines Typ-Namens zurück. Existiert 
 * der Validator nicht, wird ein Error geworfen.
 * 
 * @param {*} expectedType Name des Typs
 */
function validatorByExpectedType(expectedType) {
	const validator = VALIDATORS_BY_TYPE[expectedType] || null;
	if (validator === null) {
		throw new Error(`no validator for expected type '${expectedType}'`);
	}
	return validator;
} 